import { useRemoteSelect } from '@neo/hooks/use-remote-select'
import { Flag } from '@neo/primitives/icon'
import { Select, type SelectOption } from '@neo/primitives/select'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { phoneNumbersSelectQueryConfig } from 'v2/pages/settings/phone-numbers/queries'
import type { RPhoneNumber, RPhoneNumbersPaginated } from 'v2/types/api/settings'

interface PhoneNumberSelectProps {
  value?: string | string[]
  onChange?: (value: string | string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  label?: string
  error?: React.ReactNode
  helperText?: string
  /** If true, disable phone numbers that are already linked to a WhatsApp Business Account */
  disableWabaLinked?: boolean
  /** If true, show alias name in the display (default: true) */
  showAlias?: boolean
  /** If true, enable multi-select mode */
  multiple?: boolean
  /** Determines what value to use: 'id' (default) or 'number' (phone number with + prefix) */
  valueType?: 'id' | 'number'
}

const SelectedPhoneNumberRenderer = ({
  value,
  options,
}: {
  value: number | string[] | string
  options: SelectOption[]
}) => {
  // Handle array values (not applicable for single select but needed for type compatibility)
  if (Array.isArray(value)) {
    return <span className="truncate flex items-center gap-2">{value.join(', ')}</span>
  }

  const stringValue = String(value)

  // First check if the value exists in current options
  const existingOption = options.find((option) => option.value === stringValue)
  if (existingOption) {
    return existingOption.label
  }
  const number = parsePhoneNumberFromString(stringValue, 'US')
  return (
    <div className="flex items-center gap-2">
      <Flag iso={number?.country || ''} circle size={16} />
      <span>{number?.formatInternational()}</span>
    </div>
  )
}

export const PhoneNumberLabel = ({
  phoneNumber,
  showAlias = true,
}: {
  phoneNumber: Pick<RPhoneNumber, 'number' | 'name_tag'>
  showAlias?: boolean
}) => {
  const number = parsePhoneNumberFromString(phoneNumber.number, 'US')
  return (
    <div className="flex items-center gap-2 w-full">
      <Flag iso={number?.country || ''} circle size={16} />
      <span className="relative whitespace-nowrap overflow-hidden text-ellipsis min-w-[120px] max-w-[120px] tracking-tight">
        {number?.formatInternational()}
      </span>
      {showAlias && phoneNumber.name_tag && (
        <span className="text-secondary truncate">({phoneNumber.name_tag})</span>
      )}
    </div>
  )
}

export const PhoneNumberSelect = ({
  value,
  onChange,
  placeholder = 'Select phone number...',
  className,
  disabled,
  label,
  error,
  helperText,
  disableWabaLinked = false,
  showAlias = true,
  multiple = false,
  valueType = 'id',
}: PhoneNumberSelectProps) => {
  // Always use select query config which includes waba_linked field
  const { queryKey, fetchService } = phoneNumbersSelectQueryConfig()

  const optionExtractor = (data: unknown) => {
    const phoneData = data as RPhoneNumbersPaginated
    const options: SelectOption[] = phoneData.numbers.map((phoneNumber: RPhoneNumber) => {
      // Disable if number is linked to WABA and disableWabaLinked prop is enabled
      const isDisabled = disableWabaLinked && phoneNumber.waba_linked === true

      const parsed = parsePhoneNumberFromString(phoneNumber.number, 'US')
      return {
        label: <PhoneNumberLabel phoneNumber={phoneNumber} showAlias={showAlias} />,
        badgeLabel: parsed?.formatInternational() || phoneNumber.number,
        searchValue: `${phoneNumber.name_tag || ''} ${phoneNumber.number}`,
        value: valueType === 'number' ? phoneNumber.number : phoneNumber.id,
        disabled: isDisabled,
        tooltipContent: isDisabled
          ? 'This number is already linked to a WhatsApp Business Account'
          : undefined,
      }
    })

    return {
      options,
      hasNextPage: phoneData.numbers.length === 20, // Assuming page size of 20
      nextPage: undefined, // Let the hook handle pagination
    }
  }

  const { options, isLoading, setSearchTerm, setIsOpen, loadMore } = useRemoteSelect({
    queryKey,
    fetchService,
    optionExtractor,
    searchKey: 'pattern', // Based on the filter fields in phone-numbers/index.tsx
    pageSize: 20,
    initialPage: 1,
  })

  return (
    <Select
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      label={label}
      error={error}
      helperText={helperText}
      loading={isLoading}
      searchable
      multiple={multiple}
      onSearchChange={setSearchTerm}
      onLoadMore={loadMore}
      onOpenChange={setIsOpen}
      data-testid="select-phonenumber"
      selectedValueRenderer={SelectedPhoneNumberRenderer}
    />
  )
}
